import React from 'react';
import { FaCheck, FaRegClock } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { tryOnLook, editionAvailability } from '../../../_helpers/skinTryOn.js';
import { canBuyShopItem } from '../../../_helpers/skinShop.js';
import { isFxSkin } from '../../../_helpers/fxItems.js';
import { shopTypeLabel } from '../../../_helpers/shopPreview.js';
import SkinAvailability from '../stream/SkinAvailability.jsx';
import SkinPriceTag, { saleEnd, skinPriceLabel } from '../stream/SkinPriceTag.jsx';
import SkinTimeLimit, { useOfferClocks } from '../stream/SkinTimeLimit.jsx';
import ShopPreview, { ShopRarityTag } from './ShopPreview.jsx';

/**
 * An item's offer as one place on the page shows it: its price label, the
 * sale and sale window while it is for sale and not owned, and their clocks
 * (useOfferClocks), so this place's Get it turns off the moment either ends.
 * `onOfferEnd()` reads the shop again.
 */
export const useShopOffer = (item, onOfferEnd) => {
  const { t, format } = useLocale();
  const selling = Boolean(item?.forSale) && !item?.owned;
  const sale = selling ? item.sale ?? null : null;
  const until = selling ? item.availableUntil ?? null : null;
  const clocks = useOfferClocks(sale, until, onOfferEnd);
  return {
    price: item?.forSale ? skinPriceLabel(t, format, item.priceCents) : null,
    free: Boolean(item?.forSale) && item.priceCents === 0,
    sale,
    until,
    clocks,
  };
};

/**
 * Whether an item has Try it on (a card skin or name paint not owned yet) or
 * Preview (a vibe set or boost animation, owned or not, ENG-816).
 */
export const canTryShopItem = (item) => isFxSkin(item.skin) || (!item.owned && Boolean(tryOnLook(item.skin)));

/**
 * An item's actions, the same on a tile, the featured item and a drop card:
 * Owned, Try it on / Preview, Get it (with its price; "Get it free" at $0),
 * or Not for sale. `ids` are the data-testids of each, per place.
 */
export const ShopItemActions = ({ item, offer, busy, onTryOn, onGetIt, ids, className = '' }) => {
  const { t } = useLocale();
  const { skin } = item;
  const fx = isFxSkin(skin);
  return (
    <div className={`skin-shop-item-actions ${className}`.trim()}>
      {item.owned && (
        <span className="skin-shop-badge skin-shop-badge--owned" data-testid={ids.owned}>
          <FaCheck aria-hidden="true" /> {t('shop.owned')}
        </span>
      )}
      {canTryShopItem(item) && (
        <button
          type="button"
          className="skin-shop-action"
          onClick={() => onTryOn(item)}
          aria-label={t(fx ? 'stream.fxItem.previewLabel' : 'shop.tryItOnLabel', { name: skin.name })}
          data-testid={fx ? ids.fxTry : ids.try}
        >
          {t(fx ? 'stream.fxItem.previewAction' : 'shop.tryItOn')}
        </button>
      )}
      {canBuyShopItem(item) && (
        <button
          type="button"
          className="skin-shop-action skin-shop-action--primary"
          onClick={() => onGetIt(skin, item)}
          disabled={busy || offer.clocks.ended}
          aria-label={offer.free ? t('shop.getItFreeLabel', { name: skin.name }) : t('shop.getItLabel', { name: skin.name, price: offer.price })}
          data-testid={ids.get}
        >
          {offer.free ? t('shop.getItFree') : t('shop.getIt', { price: offer.price })}
        </button>
      )}
      {!item.owned && !item.forSale && (
        <span className="skin-shop-badge" data-testid={ids.notForSale}>{t('shop.notForSale')}</span>
      )}
    </div>
  );
};

const TILE_IDS = {
  owned: 'skin-shop-owned',
  try: undefined,
  fxTry: 'skin-shop-fx-preview',
  get: 'skin-shop-get',
  notForSale: 'skin-shop-not-for-sale',
};

/**
 * One item in the grid (ENG-823, redesigned in ENG-894): a 4:3 live preview
 * of it (ShopPreview, stamped Sold out when every edition is gone), its name
 * and rarity, its type, its price (SkinPriceTag: on sale, the struck-through
 * regular price, Sale badge and its end, ENG-832) and what's left
 * (SkinAvailability, ENG-833), when it leaves the shop (SkinTimeLimit,
 * ENG-834), then Owned / Try it on / Preview / Get it. When its sale or
 * window ends, Get it turns off and `onOfferEnd()` reads the shop again.
 * `trying` marks the item on in the wide layout's try-on panel.
 */
export const SkinTile = ({ item, ownName, busy, trying = false, onTryOn, onGetIt, onOfferEnd }) => {
  const { t } = useLocale();
  const { skin } = item;
  const offer = useShopOffer(item, onOfferEnd);
  const { clocks } = offer;
  const fx = isFxSkin(skin);

  return (
    <li
      className={`skin-shop-item vinyl-sleeve-trigger${trying ? ' skin-shop-item--trying' : ''}`}
      data-testid="skin-shop-item"
      data-skin-id={skin.id}
      data-trying={trying ? 'true' : undefined}
    >
      <ShopPreview item={item} ownName={ownName} size="tile" soldOut={!item.owned && item.soldOut} />
      <div className="skin-shop-item-row">
        <span className="skin-shop-item-name">{skin.name}</span>
        <ShopRarityTag rarity={skin.rarity} />
      </div>
      <span className={`skin-shop-item-type${fx ? ' skin-fx-type' : ''}`} data-testid={fx ? 'skin-shop-fx-type' : undefined}>
        {shopTypeLabel(t, skin)}
      </span>
      <div className="skin-shop-item-row skin-shop-item-meta">
        {offer.price && (
          <SkinPriceTag
            priceCents={item.priceCents}
            sale={offer.sale}
            clock={clocks.saleClock}
            windowEnds={clocks.shared}
            className="skin-shop-price"
            testId="skin-shop-price"
          />
        )}
        <SkinAvailability offer={item} owned={item.owned} shop />
      </div>
      {!clocks.shared && !item.soldOut && (
        <SkinTimeLimit until={offer.until} clock={clocks.windowClock} testId="skin-shop-time-limit" />
      )}
      <ShopItemActions item={item} offer={offer} busy={busy} onTryOn={onTryOn} onGetIt={onGetIt} ids={TILE_IDS} />
    </li>
  );
};

const FEATURE_IDS = {
  owned: 'skin-shop-featured-owned',
  try: 'skin-shop-featured-try',
  fxTry: 'skin-shop-featured-try',
  get: 'skin-shop-featured-get',
  notForSale: 'skin-shop-featured-not-for-sale',
};

/**
 * "11 of 250 claimed" with a meter, and the edition the buyer would get
 * (SkinAvailability), for a limited featured item.
 */
const EditionMeter = ({ item }) => {
  const { t, format } = useLocale();
  const availability = editionAvailability(item);
  if (!availability?.limited) return null;
  const issued = Math.min(availability.size, item.editionsIssued ?? 0);
  const percent = Math.round((issued / availability.size) * 100);
  return (
    <div className="skin-shop-meter" data-testid="skin-shop-featured-meter">
      <div className="skin-shop-meter-row">
        <span>{t('shop.featured.claimed', { issued: format.number(issued), size: format.number(availability.size) })}</span>
        <SkinAvailability offer={item} owned={item.owned} />
      </div>
      <span className="skin-shop-meter-track" aria-hidden="true">
        <span className="skin-shop-meter-fill" style={{ width: `${percent}%` }} />
      </span>
    </div>
  );
};

/**
 * The featured item (ENG-894; picked by pickFeaturedItem): "Featured drop",
 * its name, rarity and type, the edition meter for a limited one, its price,
 * Try it on / Preview and Get it (or Owned), beside a large live preview.
 * The hero banner shows it below 1600px, the drops band from 1600px up.
 */
export const ShopFeature = ({ item, ownName, busy, onTryOn, onGetIt, onOfferEnd }) => {
  const { t } = useLocale();
  const { skin } = item;
  const offer = useShopOffer(item, onOfferEnd);
  const { clocks } = offer;
  return (
    <article className="skin-shop-feature vinyl-sleeve-trigger" data-testid="skin-shop-featured" data-skin-id={skin.id}>
      <div className="skin-shop-feature-text">
        <p className="skin-shop-kicker">{t('shop.featured.kicker')}</p>
        <h2 className="skin-shop-feature-name">{skin.name}</h2>
        <div className="skin-shop-feature-tags">
          <ShopRarityTag rarity={skin.rarity} />
          <span className="skin-shop-feature-type">{shopTypeLabel(t, skin)}</span>
        </div>
        <EditionMeter item={item} />
        {offer.price && (
          <SkinPriceTag
            priceCents={item.priceCents}
            sale={offer.sale}
            clock={clocks.saleClock}
            windowEnds={clocks.shared}
            className="skin-shop-feature-price"
            testId="skin-shop-featured-price"
          />
        )}
        {!clocks.shared && (
          <SkinTimeLimit until={offer.until} clock={clocks.windowClock} testId="skin-shop-featured-time-limit" />
        )}
        <ShopItemActions
          item={item}
          offer={offer}
          busy={busy}
          onTryOn={onTryOn}
          onGetIt={onGetIt}
          ids={FEATURE_IDS}
          className="skin-shop-item-actions--large"
        />
      </div>
      <div className="skin-shop-feature-preview">
        <ShopPreview item={item} ownName={ownName} size="large" />
      </div>
    </article>
  );
};

const DROP_IDS = {
  owned: 'skin-shop-drop-owned',
  try: 'skin-shop-drop-try',
  fxTry: 'skin-shop-drop-try',
  get: 'skin-shop-drop-get',
  notForSale: 'skin-shop-drop-not-for-sale',
};

/** The clock line in a drop card's header: when it leaves, when its sale ends, or what's left. */
const DropNote = ({ kind, item, offer }) => {
  const { t, format } = useLocale();
  const { clocks } = offer;
  if (kind === 'leaving') {
    if (clocks.shared) return <span>{saleEnd(t, format, offer.sale, clocks.saleClock, true).text}</span>;
    return <SkinTimeLimit until={offer.until} clock={clocks.windowClock} testId="skin-shop-drop-time-limit" />;
  }
  if (kind === 'sale' && offer.sale) {
    const end = saleEnd(t, format, offer.sale, clocks.saleClock, clocks.shared);
    return (
      <span data-testid="skin-shop-drop-sale-end">
        <span aria-hidden="true">{end.text}</span>
        <span className="skin-shop-sr">{end.spoken}</span>
      </span>
    );
  }
  return <SkinAvailability offer={item} owned={item.owned} shop />;
};

/**
 * A secondary drop in the wide layout's band (ENG-894; pickSecondaryDrops):
 * "Leaving soon" with its live countdown, "On sale" with its end, or
 * "Limited run" with what's left; a 16:9 live preview, its name, rarity, type
 * and price, Try it on / Preview and Get it.
 */
export const ShopDropCard = ({ kind, item, ownName, busy, onTryOn, onGetIt, onOfferEnd }) => {
  const { t } = useLocale();
  const { skin } = item;
  const offer = useShopOffer(item, onOfferEnd);
  const { clocks } = offer;
  return (
    <article className="skin-shop-drop vinyl-sleeve-trigger" data-testid="skin-shop-drop" data-kind={kind} data-skin-id={skin.id}>
      <div className="skin-shop-drop-head">
        <span className="skin-shop-kicker">{t(`shop.drops.${kind}`)}</span>
        <span className="skin-shop-drop-note">
          <FaRegClock aria-hidden="true" />
          <DropNote kind={kind} item={item} offer={offer} />
        </span>
      </div>
      <ShopPreview item={item} ownName={ownName} size="drop" />
      <div className="skin-shop-item-row">
        <span className="skin-shop-drop-name">{skin.name}</span>
        <ShopRarityTag rarity={skin.rarity} />
      </div>
      <div className="skin-shop-item-row skin-shop-item-meta">
        <span className="skin-shop-item-type">{shopTypeLabel(t, skin)}</span>
        {offer.price && (
          <SkinPriceTag
            priceCents={item.priceCents}
            sale={offer.sale}
            clock={clocks.saleClock}
            windowEnds={clocks.shared}
            className="skin-shop-price"
            testId="skin-shop-drop-price"
            showEnd={kind === 'limited'}
          />
        )}
      </div>
      <ShopItemActions item={item} offer={offer} busy={busy} onTryOn={onTryOn} onGetIt={onGetIt} ids={DROP_IDS} />
    </article>
  );
};
